import Page from "components/Page"
import FeaturedProjectCollection from "components/FeaturedProjectCollection"
import MetadataAwareTokenView from "components/MetadataAwareTokenView"
import PlatformBadgeRow from "components/PlatformBadgeRow"
import { getProjectPlatformBadges } from "data/platforms"
import useArtBlocksProjectMetadata from "hooks/useArtBlocksProjectMetadata"
import usePageMeta from "hooks/usePageMeta"
import useWindowSize from "hooks/useWindowSize"
import { FEATURED_PROJECTS, FeaturedProjectKey, getFeaturedProject } from "config"
import { getArtworkFramePadding } from "utils/galleryLayout"
import { getFeaturedProjectPreviewImage } from "utils/socialPreview"

interface Props {
  projectKey?: FeaturedProjectKey
}

const LandingPage = ({ projectKey = "dots" }: Props) => {
  const windowSize = useWindowSize()
  const project = getFeaturedProject(projectKey) || FEATURED_PROJECTS[0]
  const projectMetadata = useArtBlocksProjectMetadata(project)
  usePageMeta(
    `${project.name} — r4v3n`,
    project.description,
    getFeaturedProjectPreviewImage(project),
    `/${project.key}`,
  )
  const metadataAspectRatio = projectMetadata.data?.aspect_ratio || project.aspectRatio || 1
  const soldCount = projectMetadata.data?.tokens_aggregate?.aggregate?.count || projectMetadata.data?.invocations
  const pagePadding = windowSize.width >= 768 ? 64 : windowSize.width >= 640 ? 48 : 40
  const artFramePadding = getArtworkFramePadding(windowSize.width)
  const artWidth =
    windowSize.width >= 1200
      ? (1200 - 48) * 0.45 - artFramePadding
    : windowSize.width >= 768
      ? (windowSize.width - 48) * 0.45 - artFramePadding
    : windowSize.width >= 640
      ? windowSize.width - pagePadding - artFramePadding
      : windowSize.width - pagePadding - artFramePadding
  const maxArtHeight = windowSize.height > 0 ? windowSize.height * 0.76 : undefined

  return (
    <Page>
      <div>
        <nav className="flex items-center gap-2 mb-10" aria-label="Breadcrumb">
          <a href="/projects" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
            Work
          </a>
          <span className="text-gallery-border">·</span>
          <span className="gallery-label text-gallery-text">{project.name}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-12">
          <div>
            <p className="gallery-label text-gallery-muted mb-4">Art Blocks Engine</p>
            <h1 className="gallery-heading mb-4">{project.name}</h1>
            <PlatformBadgeRow
              badges={getProjectPlatformBadges(project.contractAddress, project.projectId, { chainId: project.chainId })}
              className="mb-6"
            />
            <p className="text-gallery-muted text-lg leading-relaxed mb-8">{project.description}</p>

            <dl className="gallery-meta-grid mb-8">
              <div className="gallery-meta-cell">
                <dt>Network</dt>
                <dd>{project.chainName}</dd>
              </div>
              <div className="gallery-meta-cell">
                <dt>Project</dt>
                <dd>{project.projectId}</dd>
              </div>
              {soldCount !== undefined && (
                <div className="gallery-meta-cell">
                  <dt>Sold</dt>
                  <dd>{soldCount.toLocaleString()}</dd>
                </div>
              )}
              <div className="gallery-meta-cell">
                <dt>Aspect</dt>
                <dd>{metadataAspectRatio.toFixed(2)}</dd>
              </div>
              <div className="gallery-meta-cell col-span-2">
                <dt>Contract</dt>
                <dd className="break-all">{project.contractAddress}</dd>
              </div>
            </dl>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <a href={project.projectUrl} target="_blank" rel="noopener noreferrer" className="gallery-btn-primary text-sm">
                View Project
              </a>
              <a href={project.artBlocksUrl} target="_blank" rel="noopener noreferrer" className="gallery-btn text-sm">
                Art Blocks
              </a>
            </div>
          </div>

          <div className="self-start flex justify-center">
            <div className="gallery-art-shell">
              <div className="gallery-art-frame">
                <MetadataAwareTokenView
                  project={project}
                  tokenId={project.sampleTokenId}
                  width={Math.floor(Math.max(180, artWidth))}
                  fallbackAspectRatio={metadataAspectRatio}
                  maxHeight={maxArtHeight}
                  live
                  loupe
                  loupeZoom={3}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-3xl space-y-4 text-sm leading-relaxed text-gallery-text">
          {project.detailDescription.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>

        <FeaturedProjectCollection project={project} />
      </div>
    </Page>
  )
}

export default LandingPage
